import { forwardRef, RefObject, useEffect, useMemo, useRef, VideoHTMLAttributes } from 'react'
import { useMeasure, useMedia } from 'react-use'
import { useGSAP } from '@gsap/react'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import mergeRefs from 'merge-refs'

import { cn } from '@/lib/utils'
import { WPImage, WPVideo } from '@/types'

export type MediaVideoParams = {
  video_provider: 'file' | 'youtube' | 'vimeo' | 'url'
  video_mobile: WPVideo
  video_desktop: WPVideo
  poster_mobile: WPImage
  poster_desktop: WPImage
  video_url_desktop: string
  video_url_mobile: string
  controls: boolean
  autoplay: boolean
  fit: 'cover' | 'contain'
  fill: boolean
}

export type MediaVideoProps = VideoHTMLAttributes<HTMLVideoElement> & MediaVideoParams

export const MediaVideo = forwardRef<HTMLVideoElement, MediaVideoProps>(
  (
    {
      video_mobile,
      video_desktop,
      video_url_mobile,
      video_url_desktop,
      poster_mobile,
      poster_desktop,
      controls,
      autoplay,
      className,
      fit = 'cover',
      fill,
      ...videoProps
    },
    forwardRef
  ) => {
    const videoRef = useRef<HTMLVideoElement>(null!)
    const [measureRef, sizes] = useMeasure()

    const playVideo = () => videoRef.current.play()
    const stopVideo = () => videoRef.current.pause()

    const isMobile = useMedia('(max-width: 767px)', true)

    const source = useMemo(() => {
      if (isMobile && (video_mobile?.url || video_url_mobile)) {
        return video_mobile.url || video_url_mobile
      }
      return video_desktop.url || video_url_desktop
    }, [isMobile, video_mobile, video_desktop, video_url_mobile, video_url_desktop])

    useGSAP(
      () => {
        ScrollTrigger.create({
          trigger: videoRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
          markers: false,
          onEnter: playVideo,
          onLeave: stopVideo,
          onEnterBack: playVideo,
          onLeaveBack: stopVideo
        })
      },
      {
        scope: videoRef,
        dependencies: [isMobile]
      }
    )

    useEffect(() => {
      ScrollTrigger.refresh()
    }, [sizes])

    return (
      <video
        ref={mergeRefs(videoRef, forwardRef, measureRef) as RefObject<HTMLVideoElement>}
        loop={autoplay}
        muted={autoplay}
        autoPlay={autoplay}
        controls={controls}
        playsInline={true}
        preload="auto"
        className={cn(
          'relative',
          {
            'absolute left-0 top-0 h-full w-full': fill && !controls,
            'object-contain': fit === 'contain',
            'object-cover': fit === 'cover'
          },
          className
        )}
        {...videoProps}
        src={source}
        poster={isMobile ? poster_mobile?.url : poster_desktop?.url}
      />
    )
  }
)

export default MediaVideo
